import React, { useMemo } from 'react';
import { Text as ScText } from '@sitecore-jss/sitecore-jss-react';
import styles from './Tabs.module.scss';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import { useSelector } from 'react-redux';
import { getHeaderHeight } from '@/store/global';

interface FieldItem {
  id: string;
  fields: {
    title: {
      value: string;
    };
  };
}

interface Props {
  fields: {
    items?: FieldItem[];
  };
  activeTab?: number;
  onTabChange: (tabIndex: number) => void;
  className?: string;
}

const NavButton: React.FC<{ direction: 'left' | 'right'; className: string }> = ({
  direction,
  className,
}) => {
  const arrowStyle = direction === 'left' ? styles.leftArrow : styles.rightArrow;
  return (
    <span className={`${arrowStyle} ${className}`}>
      <em className={`d-flex bi-chevron-${direction}`} aria-hidden={true} />
    </span>
  );
};

const Tabs: React.FC<Props> = ({ fields, activeTab, onTabChange, className }) => {
  const tabs = fields.items;
  const sliderOptions: SwiperOptions = useMemo(
    () => ({
      modules: [Navigation, Keyboard],
      slidesPerView: 'auto',
      spaceBetween: 24,
      slidesPerGroup: 1,
      initialSlide: activeTab,
      navigation: {
        nextEl: `.next`,
        prevEl: `.prev`,
      },
      keyboard: {
        enabled: true,
      },
    }),
    [activeTab]
  );
  const headerHeight = useSelector(getHeaderHeight);
  const handleTabToggle = (tabIndex: number) => {
    if (tabIndex !== activeTab) {
      onTabChange(tabIndex);
    }
  };

  return (
    <div
      className={`${styles.tabs} ${styles.backgroundWhite} ${styles.navigationWrapper} ${className}`}
      style={{ top: `${headerHeight}px` }}
    >
      <div className={`container`}>
        <Swiper {...sliderOptions} className="w-100 position-relative d-flex align-items-center">
          {tabs?.map((tab, index) => (
            <SwiperSlide key={`${tab.id}-page-level-tab`}>
              <ScText
                tag="span"
                field={tab.fields.title}
                className={`${styles.pageLevelTabs} pb-1 ${styles.pageLevelTabsText} ${
                  index === activeTab ? styles.active : ''
                }`}
                tabIndex={0}
                onClick={() => handleTabToggle(index)}
                onKeyUp={(e: any) => e.key === 'Enter' && handleTabToggle(index)}
              />
            </SwiperSlide>
          ))}
          <NavButton className="next" direction="right" />
          <div className={styles.curtainRight}></div>
          <NavButton className="prev" direction="left" />
          <div className={styles.curtainLeft}></div>
        </Swiper>
      </div>
    </div>
  );
};

export default Tabs;
